/**
 * Flush code IndexedDB + localStorage su riconnessione.
 */

import { createCapsule } from "../api";
import { submitSegnalazioneTutela } from "../api/tutela";
import {
  listPendingCapsules,
  removePendingCapsule,
  listPendingSegnalazioni,
  removePendingSegnalazione,
  setSyncMeta,
  getSyncMeta,
} from "./offlineDb";
import {
  listTutelaSegnalazioniQueue,
  markTutelaSegnalazioneSynced,
  mergeTutelaQueuesFromIndexedDb,
} from "./tutelaStorage";
import { notifyUfficioTecnico } from "./tutelaNotifications";

export function isBrowserOnline() {
  return typeof navigator === "undefined" ? true : navigator.onLine;
}

export async function flushOfflineQueues() {
  if (!isBrowserOnline()) return { flushed: 0, errors: [] };

  const errors = [];
  let flushed = 0;

  const idbSeg = await listPendingSegnalazioni().catch(() => []);
  mergeTutelaQueuesFromIndexedDb(idbSeg);

  const capsules = await listPendingCapsules().catch(() => []);
  for (const cap of capsules) {
    try {
      const { id: _localId, createdAt: _c, pending: _p, ...payload } = cap;
      await createCapsule(payload);
      await removePendingCapsule(cap.id);
      flushed += 1;
    } catch (e) {
      errors.push({ type: "capsule", id: cap.id, message: e?.message });
    }
  }

  const lsQueue = listTutelaSegnalazioniQueue().filter((q) => !q.synced);
  for (const seg of lsQueue) {
    try {
      const apiRes = await submitSegnalazioneTutela(seg);
      if (apiRes.blocked) {
        errors.push({ type: "segnalazione", id: seg.id, message: apiRes.message });
        continue;
      }
      if (apiRes.ok) {
        markTutelaSegnalazioneSynced(seg.id);
        if (seg.id) await removePendingSegnalazione(seg.id).catch(() => {});
        flushed += 1;
      }
    } catch (e) {
      errors.push({ type: "segnalazione", id: seg.id, message: e?.message });
    }
  }

  for (const seg of idbSeg) {
    try {
      const apiRes = await submitSegnalazioneTutela(seg);
      if (apiRes.blocked) {
        errors.push({ type: "segnalazione_idb", id: seg.id, message: apiRes.message });
        continue;
      }
      if (apiRes.ok) {
        await removePendingSegnalazione(seg.id);
        notifyUfficioTecnico({
          comuneSlug: seg.comuneSlug,
          comuneName: seg.comuneName,
          segnalazioneId: seg.id,
          preview: seg.description,
        });
        flushed += 1;
      }
    } catch (e) {
      errors.push({ type: "segnalazione_idb", id: seg.id, message: e?.message });
    }
  }

  await setSyncMeta("lastFlush", {
    at: new Date().toISOString(),
    flushed,
    errorCount: errors.length,
  });

  return { flushed, errors };
}

export async function getLastSyncInfo() {
  const meta = await getSyncMeta("lastFlush").catch(() => null);
  const pendingCaps = (await listPendingCapsules().catch(() => [])).length;
  const pendingSeg =
    listTutelaSegnalazioniQueue().filter((q) => !q.synced).length +
    (await listPendingSegnalazioni().catch(() => [])).length;
  return {
    lastFlush: meta?.value || null,
    pendingCount: pendingCaps + pendingSeg,
    pendingCapsules: pendingCaps,
    pendingSegnalazioni: pendingSeg,
  };
}
